import type { ReactNode } from 'react';
import { Component } from 'react';

import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { isRTL } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import classnames from 'classnames';
import ImageGallery from 'react-image-gallery';

import CardImageMobile from '../../../hotels-utils/components-hotel-card-adjacent/CardImageMobile';
import { withI18n } from '../../../hotels-utils/services/i18n';
import NameStars from '../NameStars';

import type { I18n } from '@skyscanner-internal/landing-pages-shared-types/types-shared/hotels-components/types';
import type { ReactImageGalleryItem } from 'react-image-gallery';

import STYLES from './CardImagesSlider.module.scss';

type CardImagesSliderProps = {
  i18n: I18n;
  images: string[];
  hotelName: string;
  detailUrl: string;
  detailUrlRel?: string;
  stars?: ReactNode;
  onClick?: Function;
  hotelCardAccessibility?: boolean;
  imageRadio?: string;
};

type CardImagesSliderState = {
  currentImageIndex: number;
};

class CardImagesSlider extends Component<
  CardImagesSliderProps,
  CardImagesSliderState
> {
  static defaultProps = {
    stars: undefined,
    detailUrlRel: undefined,
    onClick: () => {},
    hotelCardAccessibility: false,
    imageRadio: undefined,
  };

  constructor(props: CardImagesSliderProps) {
    super(props);

    this.state = {
      currentImageIndex: 0,
    };
  }

  cardImageNode = (imageUrl?: string) => {
    const { imageRadio } = this.props;

    return (
      <CardImageMobile
        image={imageUrl}
        radio={imageRadio}
        className={STYLES.CardImagesSlider__img}
      />
    );
  };

  onSlide = (currentIndex: number) => {
    this.setState({ currentImageIndex: currentIndex });
  };

  hotelNameStarsNodes = (total?: number) => {
    const { hotelCardAccessibility, hotelName, stars } = this.props;

    const { currentImageIndex } = this.state;
    const isLastImage = currentImageIndex === total;

    if (hotelCardAccessibility) {
      return null;
    }
    return (
      <NameStars
        hotelName={hotelName}
        stars={stars}
        isLastImage={isLastImage}
      />
    );
  };

  renderItem = (item: ReactImageGalleryItem) => {
    const { detailUrl, detailUrlRel, hotelCardAccessibility, i18n, onClick } =
      this.props;

    const moreDetail = (
      <div className={STYLES.CardImagesSlider__moreDetailContainer}>
        <BpkText
          color={TEXT_COLORS.textOnDark}
          textStyle={TEXT_STYLES.label2}
          className={STYLES.CardImagesSlider__moreDetail}
        >
          {i18n.translate('Search_image_label')}
        </BpkText>
      </div>
    );

    return (
      <BpkCard
        className={classnames(STYLES.CardImagesSlider__card, {
          [STYLES['CardImagesSlider__card--accessibility']]:
            hotelCardAccessibility,
        })}
        padded={false}
        href={detailUrl}
        rel={detailUrlRel ? `opener ${detailUrlRel}` : 'opener'}
        target="_blank"
        data-testid="image-mobile-hotel-card"
        onClick={() => {
          onClick?.(true, null, true, 'imageOnMobile');
        }}
      >
        {this.cardImageNode(item.original) || ''}
        {/* @ts-expect-error -- legacy code */}
        {item.isLastImage && (
          <div className={STYLES.CardImagesSlider__detail}>{moreDetail}</div>
        )}
      </BpkCard>
    );
  };

  render() {
    const { detailUrl, detailUrlRel, hotelCardAccessibility, images, onClick } =
      this.props;

    if (!images || images.length === 0) {
      return (
        <div className={STYLES.CardImagesSlider}>
          <BpkCard
            className={STYLES.CardImagesSlider__card}
            padded={false}
            href={detailUrl}
            rel={detailUrlRel ? `opener ${detailUrlRel}` : 'opener'}
            target="_blank"
            data-testid="image-mobile-hotel-card"
            onClick={() => {
              onClick?.(true, null, true, 'imageOnMobile');
            }}
          >
            {images?.length > 0 && this.cardImageNode(images[0])}
          </BpkCard>
          {this.hotelNameStarsNodes()}
        </div>
      );
    }

    const subImages = images.slice(0, 3);
    const subImagesNumber = subImages.length;
    const lastItem = {
      original: subImages.slice(subImagesNumber - 1)[0],
      isLastImage: true,
      url: detailUrl,
    };
    const items = subImages.map((url, i) => ({
      isFirstImage: i === 0,
      original: url,
      isLastImage: false,
      url: detailUrl,
    }));
    let showItems;
    if (items.length > 1) {
      showItems = [...items, lastItem];
    } else {
      showItems = [...items];
    }

    return (
      <div
        className={classnames(STYLES.CardImagesSlider, {
          [STYLES['CardImagesSlider--accessibility']]: hotelCardAccessibility,
        })}
      >
        <ImageGallery
          showNav={false}
          showThumbnails={false}
          showFullscreenButton={false}
          showPlayButton={false}
          isRTL={isRTL()}
          infinite={isRTL()}
          items={showItems}
          renderItem={this.renderItem}
          showBullets={showItems.length > 1}
          onSlide={this.onSlide}
          // @ts-expect-error -- legacy code
          bulletClass={STYLES.CardImagesSlider__bullet}
        />
        {this.hotelNameStarsNodes(subImagesNumber)}
      </div>
    );
  }
}

export default withI18n(CardImagesSlider);
